import { Button } from '@/shared/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { useCan } from '@skewb/access-control/react';
import {
  Eye,
  KeyRound,
  MoreHorizontal,
  Pencil,
  Shield,
  TerminalSquare,
  UserCheck,
  UserX,
} from 'lucide-react';

interface UserActionsMenuProps {
  isActive?: boolean;
  hasCognitoAccount?: boolean;
  /**
   * Whether the *current actor* is allowed to trigger a password reset for
   * this row. Defaults to true to preserve callers that haven't been updated
   * yet — but those callers will still hit the backend's 403 on submit.
   */
  canResetPassword?: boolean;
  /**
   * Tooltip shown when the password-reset action is gated (e.g. lower tier).
   */
  resetPasswordDisabledReason?: string;
  /**
   * Show the dev-only "Set Password Directly" item. Hidden by default; the
   * caller toggles this on in development builds via `import.meta.env.DEV`.
   */
  showDevSetPassword?: boolean;
  onView?: () => void;
  onEdit?: () => void;
  onResetPassword?: () => void;
  onSetPasswordDev?: () => void;
  onManageRoles?: () => void;
  onDeactivate?: () => void;
  onReactivate?: () => void;
}

export function UserActionsMenu({
  isActive = true,
  hasCognitoAccount = true,
  canResetPassword = true,
  resetPasswordDisabledReason,
  showDevSetPassword = false,
  onView,
  onEdit,
  onResetPassword,
  onSetPasswordDev,
  onManageRoles,
  onDeactivate,
  onReactivate,
}: UserActionsMenuProps) {
  const canManageRoles = useCan('create', 'AccessControl');
  // All user mutations (edit, (de/re)activate, password reset/set) are guarded by
  // `update User` on the backend — hide the affordances when the actor lacks it so they
  // never click through to a 403 (WRK-5474). View Details stays (reads are public).
  const canUpdateUser = useCan('update', 'User');
  const resetDisabled = !hasCognitoAccount || !canResetPassword;
  const resetTitle = !hasCognitoAccount
    ? 'User has no Cognito account'
    : !canResetPassword
      ? resetPasswordDisabledReason || "You don't have permission to reset this user's password"
      : undefined;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
          aria-label="Open actions menu"
          onClick={(e) => e.stopPropagation()}
        >
          <MoreHorizontal className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56" onClick={(e) => e.stopPropagation()}>
        {onView && (
          <DropdownMenuItem onClick={onView}>
            <Eye className="w-4 h-4 mr-2" />
            View Details
          </DropdownMenuItem>
        )}
        {canUpdateUser && onEdit && (
          <DropdownMenuItem onClick={onEdit}>
            <Pencil className="w-4 h-4 mr-2" />
            Edit User
          </DropdownMenuItem>
        )}
        {canUpdateUser && onResetPassword && (
          <DropdownMenuItem onClick={onResetPassword} disabled={resetDisabled} title={resetTitle}>
            <KeyRound className="w-4 h-4 mr-2" />
            Reset Password
          </DropdownMenuItem>
        )}
        {canUpdateUser && showDevSetPassword && onSetPasswordDev && (
          <DropdownMenuItem
            onClick={onSetPasswordDev}
            disabled={!hasCognitoAccount || !canResetPassword}
            title={
              !hasCognitoAccount
                ? 'User has no Cognito account'
                : !canResetPassword
                  ? resetPasswordDisabledReason ||
                    "You don't have permission to set this user's password"
                  : 'Dev-only: sets password directly without email'
            }
            className="text-yellow-700 dark:text-yellow-400"
          >
            <TerminalSquare className="w-4 h-4 mr-2" />
            Set Password Directly (dev)
          </DropdownMenuItem>
        )}
        {canManageRoles && onManageRoles && (
          <DropdownMenuItem onClick={onManageRoles}>
            <Shield className="w-4 h-4 mr-2" />
            Manage Roles
          </DropdownMenuItem>
        )}
        {canUpdateUser && (onDeactivate || onReactivate) && <DropdownMenuSeparator />}
        {canUpdateUser && isActive && onDeactivate && (
          <DropdownMenuItem
            onClick={onDeactivate}
            className="text-red-600 dark:text-red-400 focus:text-red-700"
          >
            <UserX className="w-4 h-4 mr-2" />
            Deactivate
          </DropdownMenuItem>
        )}
        {canUpdateUser && !isActive && onReactivate && (
          <DropdownMenuItem onClick={onReactivate} className="text-green-600 dark:text-green-400">
            <UserCheck className="w-4 h-4 mr-2" />
            Reactivate
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
